'use client';

import type { CategoryDto } from '@/queries/categories/categories.schema';
import { Icon } from '@/components/entities/Sprite';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
} from '@/components/ui/Breadcrumb/breadcrumb';
import { CATEGORY_ICONS } from '@/consts/CategoryIcons';
import { Env } from '@/consts/Env';
import { AppRoutes } from '@/i18n/constants';
import { Link } from '@/i18n/routing';
import { capitalizeFirstLetter, capitalizeWords } from '@/utils/texts.utils';
import BreadcrumbSeo from './components/BreadcrumbSeo';

type GameBreadcrumbProps = {
  gameName?: string;
  gameId?: string;
  category?: CategoryDto;
};

const CustomBreadCrumb = ({ gameName, category }: GameBreadcrumbProps) => {
  if (!category) {
    return null;
  }

  const capitalizedCategory = capitalizeFirstLetter(category?.name);
  const capitalizedGameName = capitalizeFirstLetter(gameName ?? '');

  const seoBreadcrumbs = [
    {
      name: capitalizedCategory,
      item: `${Env.NEXT_PUBLIC_APP_URL}${AppRoutes.categoryIdPaged(category.id)}`,
    },
  ];

  if (gameName) {
    seoBreadcrumbs.push({
      name: capitalizedGameName,
      item: `${Env.NEXT_PUBLIC_APP_URL}${AppRoutes.categoryIdPaged(category.id)}/${encodeURIComponent(gameName)}`,
    });
  }
  const iconName = CATEGORY_ICONS[category.id];
  return (
    <>
      <BreadcrumbSeo breadcrumbs={seoBreadcrumbs} />
      {/* Hidden from users on mobile but kept in the DOM for crawlers; visible on desktop. The JSON-LD above is untouched. */}
      <div className="sr-only lg:not-sr-only relative overflow-hidden -mr-4">

        <Breadcrumb className="flex items-center gap-1 mb-5.5 relative overflow-x-auto hide-scrollbar md:overflow-x-visible pr-4">
          <BreadcrumbItem>
            {iconName && <Icon name={iconName} className="size-6 text-c300" />}
            <BreadcrumbLink asChild>
              <Link href={AppRoutes.categoryIdPaged(category.id)}>
                <span className="text-lg whitespace-nowrap font-medium leading-7 text-c300">{capitalizeFirstLetter(category?.name)}</span>
              </Link>
            </BreadcrumbLink>
            <BreadcrumbSeparator>
              <Icon name="arrow-separator" className="size-4 text-c300" />
            </BreadcrumbSeparator>
          </BreadcrumbItem>

          <BreadcrumbItem>
            <span className="text-lg font-medium leading-7 text-white whitespace-nowrap">
              {capitalizeWords(gameName ?? '')}
            </span>
          </BreadcrumbItem>
        </Breadcrumb>
      </div>

    </>
  );
};

export default CustomBreadCrumb;
